import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlusCircle, Save2, Trash, PersonLinesFill, CardHeading, CreditCard2Front, GeoAlt, EnvelopeAt, Telephone, CardText, GlobeAmericas, Geo, Map, CardList, MapFill } from 'react-bootstrap-icons'
import { Button, Form, Row, Col } from 'react-bootstrap'

import PartnersService from '../services/PartnersService'

import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import partnersConfig from '../config/partners'

const PartnersForm = (props) => {

    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [name, setName] = useState('')
    const [vat, setVat] = useState('')
    const [pif, setPif] = useState('')
    const [type, setType] = useState('')
    const [address, setAddress] = useState('')
    const [zipCode, setZipCode] = useState('')
    const [city, setCity] = useState('')
    const [province, setProvince] = useState('')
    const [country, setCountry] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [emailErrorKey, setEmailErrorKey] = useState('');
    const [description, setDescription] = useState('')
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    const getData = async () => {
        await PartnersService.getPartner(elementId).then(
            response => {
                setName(response.data.name || '')
                setVat(response.data.vat || '')
                setPif(response.data.pif || '')
                setType(response.data.type || '')
                setAddress(response.data.address || '')
                setZipCode(response.data.zipCode || '')
                setCity(response.data.city || '')
                setProvince(response.data.province || '')
                setCountry(response.data.country || '')
                setPhone(response.data.phone || '')
                setEmail(response.data.email || '')
                setDescription(response.data.description || '')
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value

        if (name === 'email') {
            setEmailErrorKey(''); 
        }

        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                setName(value)
                break
            case 'vat':
                setVat(value)
                break
            case 'pif':
                setPif(value)
                break
            case 'type':
                setType(value)
                break
            case 'address':
                setAddress(value)
                break
            case 'zipCode':
                setZipCode(value)
                break
            case 'city':
                setCity(value)
                break
            case 'province':
                setProvince(value)
                break
            case 'country':
                setCountry(value)
                break
            case 'phone':
                setPhone(value)
                break
            case 'email':
                setEmail(value)
                break
            case 'description':
                setDescription(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setEmailErrorKey('');

        const form = e.currentTarget
        if (!form.checkValidity()) {
            setFormValid(true)
            return
        }

        const partner = {
            name,
            vat,
            pif,
            type,
            address,
            zipCode,
            city,
            province,
            country,
            phone,
            email,
            description,
        }

        try {
            if (elementId && action === "edit") {
                await PartnersService.updatePartner(partner, elementId)
            } else if (action === "add") {
                await PartnersService.addPartner(partner)
            } else if (action === "delete") {
                await PartnersService.deletePartner(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            const serverError = error.response?.data;
        
            if (serverError?.errorKey === "partnersEmailAlreadyExists") {
                setEmailErrorKey(serverError.errorKey); // Guardamos la key para el rojo
                setFormValid(true); // Activamos la validación visual
            } else {
                // Error genérico para el banner
                setShowMsg({ text: error.response?.data?.msg || "An error occurred", style: 'danger' });
                setShowAlert(true);
            }

        } finally {
            setFormValid(true)
        }
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="name">
                    <PersonLinesFill /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersName" defaultMessage="Name" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="name"
                        value={name}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="partnersNameInvalid" defaultMessage="Provide a partner name" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="vat">
                            <CardHeading /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersVAT" defaultMessage="VAT number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="vat"
                                value={vat}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersVATInvalid" defaultMessage="Provide a valid VAT number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="pif">
                            <CardHeading /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersPIF" defaultMessage="PIF number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="pif"
                                value={pif}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            // required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersPIFInvalid" defaultMessage="Provide a valid PIF number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-4" controlId="type">
                    <CardList /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersType" defaultMessage="Type" />:</Form.Label>
                    <Form.Control
                        as="select"
                        name="type"
                        value={type}
                        onChange={(e) => {
                            handleInputChange({ target: { name: e.target.name, value: e.target.value } });
                        }}
                        disabled={inputDisabled && ("disabled")}
                        required
                    >
                        <option key='blank_type' value=''><FormattedMessage id="textSelect" defaultMessage="Select" /> ...</option>
                        {partnersConfig.PARTNER_TYPES.map((partnerType) => (
                            <option key={partnerType} value={partnerType}>
                                {partnerType}
                            </option>
                        ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="partnersTypeInvalid" defaultMessage="Select an partner type" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="address">
                    <GeoAlt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersAddress" defaultMessage="Address" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="address"
                        value={address}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="partnersAddressInvalid" defaultMessage="Provide an address" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="zipCode">
                            <Geo /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersZipCode" defaultMessage="ZIP Code" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="zipCode"
                                value={zipCode}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersZipCodeInvalid" defaultMessage="Provide a ZIP code" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="city">
                            <Map /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersCity" defaultMessage="City" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="city"
                                value={city}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersCityInvalid" defaultMessage="Provide a city" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="province">
                            <MapFill /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersProvince" defaultMessage="Province" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="province"
                                value={province}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersProvinceInvalid" defaultMessage="Provide a province" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="country">
                            <GlobeAmericas /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersCountry" defaultMessage="Country" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="country"
                                value={country}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersCountryInvalid" defaultMessage="Provide a country" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="phone">
                            <Telephone /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersPhoneNumber" defaultMessage="Phone number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={phone}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="partnersPhoneNumberInvalid" defaultMessage="Provide a phone number" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="email">
                            <EnvelopeAt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersEmail" defaultMessage="E-mail" />:</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={email}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                className={emailErrorKey !== '' ? 'is-invalid border-danger' : (formValid ? 'is-valid' : '')}
                            // required="required"
                                pattern="^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
                            />
                            {emailErrorKey !== '' ? (
                                <div className="text-danger small mt-1" style={{ display: 'block', fontWeight: 'normal' }}>
                                    <FormattedMessage id={emailErrorKey} />
                                </div>
                            ) : (
                            <Form.Control.Feedback type="invalid">
                                    <FormattedMessage id="partnersEmailInvalid" defaultMessage="Provide a valid Email address" />
                            </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3" controlId="description">
                    <CardText /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="partnersDescription" defaultMessage="Description" />:</Form.Label>
                    <Form.Control
                        name="description"
                        value={description}
                        as="textarea"
                        disabled={inputDisabled && ("disabled")}
                        rows={3}
                        onChange={handleInputChange}
                    />
                </Form.Group>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default PartnersForm